import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup} from '@mui/material'
import {useTableParams} from '../../core/hooks/table-params'
import Loader from '../../core/components/Loader'
import {IColumn, Table} from '../../core/components/Table'
import {Pagination} from '../../core/components/Pagination'
import {IPlannedPayment, PlannedPaymentStatus} from '../../core/interfaces'
import {getAllPlannedPayments} from '../../services/planned-payments'

const STATUS_COLOR: Record<PlannedPaymentStatus, string> = {
    PAID: '#388e3c',
    TO_BE_PAID: '#1976d2',
    OVERDUE: '#d32f2f',
}

const STATUS_LABEL: Record<PlannedPaymentStatus, string> = {
    PAID: 'Paid',
    TO_BE_PAID: 'To Be Paid',
    OVERDUE: 'Overdue',
}

export const PlannedPaymentsListPage: React.FC = (): JSX.Element => {
    const {page, setPage, size, setSize, totalElements, setTotalElements, sortBy, setSortBy} =
        useTableParams({defaultSortBy: 'paymentDate'})

    const [loading, setLoading] = useState(false)
    const [payments, setPayments] = useState<IPlannedPayment[]>([])
    const [showPaid, setShowPaid] = useState(false)
    const [statusFilter, setStatusFilter] = useState<'overdue' | 'next' | null>(null)

    const searchParams = useRef<{userName?: string; kidName?: string}>({})
    const timer = useRef<NodeJS.Timeout | null>(null)

    const getData = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getAllPlannedPayments({
                page,
                size,
                sortBy,
                sortDirection: 'ASC',
                userName: searchParams.current.userName || undefined,
                kidName: searchParams.current.kidName || undefined,
                showPaid,
                filterOverdue: statusFilter === 'overdue',
                filterNext: statusFilter === 'next',
            })
            setTotalElements(response.data.totalElements)
            setPayments(response.data.content)
            setPage(page + 1)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }, [page, size, sortBy, showPaid, statusFilter])

    useEffect(() => {
        getData()
    }, [page, size, sortBy, showPaid, statusFilter])

    const handleSortBy = (sort: string) => setSortBy(sort === sortBy ? 'paymentDate' : sort)

    const handleSearch = useCallback((col: IColumn, q: string) => {
        if (timer.current) clearTimeout(timer.current)
        searchParams.current = {...searchParams.current, [col.accessor]: q}
        timer.current = setTimeout(async () => {
            setLoading(true)
            try {
                const {data} = await getAllPlannedPayments({
                    page: 0,
                    size,
                    sortBy,
                    sortDirection: 'ASC',
                    userName: searchParams.current.userName || undefined,
                    kidName: searchParams.current.kidName || undefined,
                    showPaid,
                    filterOverdue: statusFilter === 'overdue',
                    filterNext: statusFilter === 'next',
                })
                setPayments(data.content)
                setTotalElements(data.totalElements)
                setPage(data.pageNumber + 1)
            } catch (e) {
                console.error(e)
            } finally {
                setLoading(false)
            }
        }, 600)
    }, [size, sortBy, showPaid, statusFilter])

    const handleStatusFilter = (_: React.MouseEvent<HTMLElement>, value: 'overdue' | 'next' | null) => {
        setStatusFilter(value)
        setPage(1)
    }

    const columns: IColumn[] = [
        {title: 'User Name', accessor: 'userName', sortable: false, searchable: true},
        {title: 'Kid Name', accessor: 'kidName', sortable: false, searchable: true},
        {
            title: 'Amount',
            accessor: '',
            render: (row: IPlannedPayment) => <span>{row.amount} RON</span>,
        },
        {
            title: 'Payment Date',
            accessor: 'paymentDate',
            sortable: true,
            searchable: false,
        },
        {
            title: 'Interval',
            accessor: '',
            render: (row: IPlannedPayment) => (
                <span style={{whiteSpace: 'nowrap'}}>
                    {row.intervalStart} — {row.intervalEnd}
                </span>
            ),
        },
        {
            title: 'Status',
            accessor: '',
            render: (row: IPlannedPayment) => (
                <span style={{fontWeight: 600, color: STATUS_COLOR[row.status]}}>
                    {STATUS_LABEL[row.status]}
                </span>
            ),
        },
    ]

    return (
        <div style={{padding: 20, backgroundColor: '#F8F8F8', position: 'relative'}}>
            {loading && <Loader />}

            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <h2>Planned Payments</h2>
                <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={handleStatusFilter}
                        size="small"
                    >
                        <ToggleButton value="overdue" color="error">Overdue</ToggleButton>
                        <ToggleButton value="next" color="primary">Next Payments</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showPaid}
                                onChange={e => { setShowPaid(e.target.checked); setPage(1) }}
                            />
                        }
                        label="Show Paid"
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table
                    handleSearch={handleSearch}
                    isCheck={[]}
                    columns={columns}
                    handleSortBy={handleSortBy}
                    data={payments}
                />
            </div>
            <Pagination
                size={size}
                totalElements={totalElements}
                setSize={setSize}
                page={page + 1}
                changePage={newPage => setPage(newPage)}
            />
        </div>
    )
}
